import { toast as sonnerToast } from "sonner";
import type { ReactNode } from "react";

type Variant = "default" | "destructive" | "success";

interface ToastInput {
  title?: ReactNode;
  description?: ReactNode;
  variant?: Variant;
  duration?: number;
  action?: { label: string; onClick: () => void };
  [key: string]: unknown;
}

interface ToastHandle {
  id: string | number;
  dismiss: () => void;
  update: (input: ToastInput) => void;
}

function nodeToString(node: ReactNode): string {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  return "";
}

function toast(input: ToastInput): ToastHandle {
  const { title, description, variant, duration, action } = input;
  const opts: Parameters<typeof sonnerToast>[1] = {
    description: description != null ? (description as ReactNode) : undefined,
    duration,
    action,
  };
  const titleNode: ReactNode = title ?? "";
  let id: string | number;
  if (variant === "destructive") {
    id = sonnerToast.error(titleNode as string | ReactNode, opts);
  } else if (variant === "success") {
    id = sonnerToast.success(titleNode as string | ReactNode, opts);
  } else {
    id = sonnerToast(titleNode as string | ReactNode, opts);
  }
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastInput) => {
      const titleStr = nodeToString(next.title);
      sonnerToast(titleStr, {
        id,
        description: next.description != null ? (next.description as ReactNode) : undefined,
        duration: next.duration,
        action: next.action,
      });
    },
  };
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) =>
      toastId !== undefined ? sonnerToast.dismiss(toastId) : sonnerToast.dismiss(),
    toasts: [] as never[],
  };
}

export { useToast, toast };
